import type { Folder } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { cacheKeyFor, Store } from '@warp-drive/core';
import type { FindRecordOptions } from '@warp-drive/core/types/request';
import { createRecord, findRecord } from '@warp-drive/utilities/json-api';

import { jsonApiOptions } from '../../../lib/json-api.ts';

/**
 * Finds a folder by path
 * @param path - The folder path (e.g., |clientId or |clientId|subfolder)
 * @returns JSON API request to find the folder with its files and subfolders
 */
export function findFolderWithContents(path: string) {
  return findRecord<Folder>(DataTypeContent.Folder, path, {
    ...jsonApiOptions,
    include: ['files', 'folders'],
  } as FindRecordOptions);
}

/**
 * Finds a folder by id
 * @param folderId - The id of the folder to find
 * @returns JSON API request to find the folder
 */
export function findFolder(folderId: string) {
  return findRecord<Folder>(DataTypeContent.Folder, folderId, {
    ...jsonApiOptions,
  } as FindRecordOptions);
}

/**
 * Creates a folder record via JSON:API POST
 * @param folder - The folder record to create
 * @param store - The store instance
 * @returns JSON API request to create the folder
 */
export function createFolder(folder: Folder, store: Store) {
  const init = createRecord(folder, {
    ...jsonApiOptions,
  });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(folder)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}
